<template>
  <v-form ref="formRef" class="pump-shared-form method-form" @submit.prevent="">
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-calculator-variant-outline</v-icon>
        <span>Berechnungsart</span>
      </div>

      <!-- Calculation Method -->
      <v-autocomplete
        v-model="form.calculationMethod"
        :items="calculationMethods"
        item-title="text"
        item-value="value"
        label="Berechnungsmethode"
        :error-messages="validationErrors?.calculation_method"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.calculation_method" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.calculation_method }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!--
        Rechenmethode (WP-P2 / decision D-1) is NOT selectable: the legacy method is
        soft-deprecated and cannot be chosen for new work, so the picker only ever offered one
        option. `calc_version` is still mapped in `fields` below and still travels with the
        payload — dropping it would let the model default rewrite a stored legacy measure to the
        new method and restate its published result, which is the one thing calc_version exists
        to prevent.
      -->
      <div class="shared-form-hint text-body-2 text-medium-emphasis">
        Die Auswahl bestimmt, ob die Pumpe über Leistungskennlinie oder Systemwirkungsgrad bewertet wird.
      </div>

      <div class="shared-form-hint text-body-2 text-medium-emphasis">
        Die <strong>neue Rechenmethode</strong> rechnet seit dem 09/2026 nach DIN V 18599.
      </div>

      <!--
        Shown only for measures stored before the switch. Without it the line above would
        misdescribe exactly those measures, which is the one case the removed picker used to
        make visible.
      -->
      <div
        v-if="form.calcVersion === 1"
        class="shared-form-hint text-body-2 text-medium-emphasis"
      >
        Diese Maßnahme wurde mit der <strong>alten Rechenmethode</strong> erstellt und behält sie,
        damit erstellte Berichte reproduzierbar bleiben.
      </div>

      <!--
        `calc_version` no longer has an input to carry its error message. The backend can still
        reject it (`_validate_not_legacy_calc_version`), so surface it here rather than letting
        the save fail with nothing on screen.
      -->
      <div v-if="validationErrors?.calc_version" class="shared-form-hint text-body-2 text-error">
        {{ [].concat(validationErrors.calc_version).join(' ') }}
      </div>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'

// ============================================================
// Props & Emits
//
// NOTE: sharedData is intentionally omitted here.
// Shared forms are the SOURCE of sharedData — they never consume it.
// useBaseForm with shared=true never reads sharedData in data.get(),
// so passing it would be misleading and unused.
// ============================================================

const props = defineProps({
  modelValue:       { type: Object,  default: () => ({}) },
  reference:        { type: Boolean, default: false },
  planned:          { type: Boolean, default: false },
  otherData:        { type: Object,  default: () => ({}) },
  validationErrors: { type: Object,  default: () => ({}) },
  methodValue:      { type: String,  default: '' },
})

const emit = defineEmits(['update:modelValue'])

// ============================================================
// Field mapping (frontend key -> backend key)
// ============================================================

const fields = {
  calculationMethod: 'calculation_method',
  calcVersion:       'calc_version',
}

// ============================================================
// Base form composable (shared = TRUE)
//
// shared=true means data.get() returns only this form's own fields
// plus preserved metadata. It does NOT merge any sharedData on top,
// because this form IS the shared data that normal child forms merge from.
//
// The default for calculationMethod is declared here so useBaseForm
// initialises the field correctly when no backend value exists yet.
// No additional watcher is needed — useBaseForm handles the fallback.
// ============================================================

const {
  form,
  hint,
  fetchHints,
} = useBaseForm({
  value:       computed(() => props.modelValue),
  fields,
  defaults: {
    calculationMethod: 'system_efficiency',
    // Mirrors PumpCommon.CALC_METHOD_18599 — new measures start on the conformant method.
    calcVersion: 2,
  },
  reference:   computed(() => props.reference),
  planned:     computed(() => props.planned),
  shared:      computed(() => true),
  otherData:   computed(() => props.otherData),
  sharedData:  computed(() => ({})),   // unused by shared forms, passed as empty guard
  methodValue: computed(() => props.methodValue),
  emit:        (e, p) => emit(e, p),
})

// ============================================================
// Static choices
//
// Fixed values — no API call needed.
// ============================================================

// Ordered by decreasing recommendation, mirroring Pump2.CALCULATION_METHOD_CHOICES.
// All three are safe to use; none is deprecated.
const calculationMethods = [
  { value: 'performance_curve', text: 'Pumpenkennlinien Leistung' },
  { value: 'eei',               text: 'Nassläufer: EEI (EU 641/2009)' },
  { value: 'system_efficiency', text: 'Systemwirkungsgrad' },
]

// `calc_version` has no picker (see the template): the legacy method is soft-deprecated and
// cannot be chosen for new work, so the list only ever held one selectable entry. The backend
// enforces that in `ActionSerializer._validate_not_legacy_calc_version` — removing the control
// changes what is offered, not what is allowed.

// ============================================================
// Expose (for parent form validation)
// ============================================================

const formRef      = ref(null)
const validateForm = () => formRef.value?.validate?.()

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

// ============================================================
// Lifecycle
// ============================================================

onMounted(async () => {
  await fetchHints()
})
</script>

<style scoped lang="scss">
.pump-shared-form {
  width: 100%;
}

/* Field styling */
.pump-shared-form :deep(.v-field) {
  border-radius: 18px;
}

.pump-shared-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 0;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Hint icons */
.pump-shared-form :deep(.v-input__append),
.pump-shared-form :deep(.v-field__append-inner) {
  align-items: center;
}

.pump-shared-form :deep(.v-input__append .v-icon),
.pump-shared-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

/* Small explanatory text */
.shared-form-hint {
  padding-left: 0.65rem;
  line-height: 1.45;
}

/* Disabled fields */
.pump-shared-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }
}
</style>
